import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DEFAULT_POSITION, boxKey, boxesOf, isPlaced, optionLabel, parseBoxKey, positionable, typeMeta } from './fieldTypes'
import { FIELD_DRAG_TYPE } from './StructurePanel'
import TypePicker from './TypePicker'
import { interpolate, useT } from './translations'

const ZOOMS      = [0.5, 0.75, 1, 1.25, 1.5, 2, 3]
const SNAP_PX    = 6
const MIN_SIZE   = 4
const HANDLES    = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']
const FONT_STACK = { Helvetica: 'Helvetica, Arial, sans-serif', 'Times-Roman': '"Times New Roman", Times, serif', Courier: '"Courier New", Courier, monospace' }

const isTyping = (target) => ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable

const normalize = ({ x1, y1, x2, y2 }) => ({
  x1: Math.min(x1, x2), y1: Math.min(y1, y2), x2: Math.max(x1, x2), y2: Math.max(y1, y2)
})

const snapAxis = (moving, targets, threshold) => {
  let best = null
  moving.forEach((value) => {
    targets.forEach((target) => {
      const offset = target - value
      if (Math.abs(offset) <= threshold && (!best || Math.abs(offset) < Math.abs(best.offset))) {
        best = { offset, at: target }
      }
    })
  })
  return best
}

const BoxContent = ({ field, position, zoom, showLabels }) => {
  const style = {
    fontFamily: FONT_STACK[position.font] || FONT_STACK.Helvetica,
    fontWeight: position.bold ? 700 : 400,
    fontStyle:  position.italic ? 'italic' : 'normal',
    color:      position.fill_color || '#000000',
    fontSize:   position.font_size_auto ? undefined : `${position.font_size * zoom}px`
  }

  if (field.data_type === 'table' && field.table_columns) {
    const config  = field.table_columns
    const slots   = (config.pdf_rows || 1) + (config.print_header ? 1 : 0) + (config.show_totals ? 1 : 0)
    const columns = config.columns || []
    let left = 0
    return (
      <div className="fb-box-table">
        {Array.from({ length: slots - 1 }, (_, index) => (
          <div key={`r${index}`} className="fb-box-line" style={{ top: `${((index + 1) * 100) / slots}%` }} />
        ))}
        {columns.slice(0, -1).map((column) => {
          left += column.width || 100 / columns.length
          return <div key={column.key} className="fb-box-column" style={{ left: `${left}%` }} />
        })}
        {showLabels && <span className="fb-box-label" style={style}>{field.name}</span>}
      </div>
    )
  }

  if (['yes_no', 'checkbox'].includes(field.data_type)) {
    return <span className="fb-box-tick" style={style}>✕</span>
  }

  return showLabels ? <span className="fb-box-label" style={style}>{field.name}</span> : null
}

const OptionContent = ({ field, option, showLabels }) => {
  const t = useT()
  return (
    <>
      <span className="fb-box-tick">✕</span>
      {showLabels && <span className="fb-option-tag">{optionLabel(field, option, t)}</span>}
    </>
  )
}

const Box = memo(({ box, selected, active, handles, zoom, showLabels, handlers }) => {
  const t = useT()
  const { field, option } = box
  const position = { ...DEFAULT_POSITION, ...box.position }
  const label = option === null ? field.name : `${field.name} · ${optionLabel(field, option, t)}`

  return (
    <div
      className={`fb-box tone-${typeMeta(field).tone}${selected ? ' selected' : ''}${active ? ' active' : ''}${option !== null ? ' is-option' : ''}`}
      style={{
        left:   position.x1 * zoom,
        top:    position.y1 * zoom,
        width:  Math.max(1, (position.x2 - position.x1) * zoom),
        height: Math.max(1, (position.y2 - position.y1) * zoom)
      }}
      title={label}
      onMouseDown={(event) => handlers.move(event, box)}
    >
      {option === null
        ? <BoxContent field={field} position={position} zoom={zoom} showLabels={showLabels} />
        : <OptionContent field={field} option={option} showLabels={showLabels} />}
      {handles && HANDLES.map((handle) => (
        <span key={handle} className={`fb-handle h-${handle}`} onMouseDown={(event) => handlers.resize(event, box, handle)} />
      ))}
    </div>
  )
}, (before, after) => (
  before.box.field === after.box.field && before.box.position === after.box.position && before.box.key === after.box.key &&
  before.selected === after.selected && before.active === after.active && before.handles === after.handles &&
  before.zoom === after.zoom && before.showLabels === after.showLabels && before.handlers === after.handlers
))

const PdfCanvas = ({ pages, fields, selection, readOnly, styleClipboard, activeBox, placing, actions }) => {
  const t = useT()
  const scrollRef = useRef(null)
  const pageRefs  = useRef([])
  const interaction = useRef(null)

  const [zoom, setZoom]           = useState(1)
  const [tool, setTool]           = useState('select')
  const [sizes, setSizes]         = useState({})
  const [drafts, setDrafts]       = useState({})
  const [guides, setGuides]       = useState(null)
  const [marquee, setMarquee]     = useState(null)
  const [pending, setPending]     = useState(null)
  const [showLabels, setShowLabels] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [dropPage, setDropPage]   = useState(null)

  const boxes = useMemo(
    () => fields.filter(positionable).flatMap(boxesOf).map((box) => (drafts[box.key] ? { ...box, position: drafts[box.key] } : box)),
    [fields, drafts]
  )
  const boxByKey = useMemo(() => Object.fromEntries(boxes.map((box) => [box.key, box])), [boxes])

  const single = selection.length === 1 ? fields.find((field) => field.id === selection[0]) : null
  const selectedBoxes = boxes.filter((box) => selection.includes(box.field.id))
  const workingBoxes = single && selectedBoxes.length > 1 && boxByKey[activeBox] &&
    parseBoxKey(activeBox).fieldId === single.id ? [boxByKey[activeBox]] : selectedBoxes

  const target = !readOnly && (placing ||
    (single && positionable(single) && !isPlaced(single) ? { fieldId: single.id, option: null } : null))

  const pointOnPage = (event, pageNumber) => {
    const rect = pageRefs.current[pageNumber - 1].getBoundingClientRect()
    return { x: (event.clientX - rect.left) / zoom, y: (event.clientY - rect.top) / zoom }
  }

  const pageSize = (pageNumber) => sizes[pageNumber] || { w: 595, h: 842 }

  const targetsFor = (pageNumber, excluded) => {
    const size = pageSize(pageNumber)
    const xs = [0, size.w / 2, size.w]
    const ys = [0, size.h / 2, size.h]
    boxes.forEach(({ key, position }) => {
      if (excluded.includes(key) || position.page_number !== pageNumber) { return }
      xs.push(position.x1, (position.x1 + position.x2) / 2, position.x2)
      ys.push(position.y1, (position.y1 + position.y2) / 2, position.y2)
    })
    return { xs, ys }
  }

  const startMove = (event, box) => {
    if (event.button !== 0) { return }
    event.stopPropagation()
    const field = box.field

    const additive = event.shiftKey || event.metaKey || event.ctrlKey
    if (additive) {
      actions.setSelection(selection.includes(field.id) ? selection.filter((id) => id !== field.id) : [...selection, field.id])
      return
    }
    let ids = selection
    if (!selection.includes(field.id)) {
      ids = [field.id]
      actions.setSelection(ids)
    }
    actions.setActiveBox(box.key)
    if (readOnly) { return }

    // Several fields move together; the boxes of one field move one by one
    const page = box.position.page_number
    const moving = ids.length > 1
      ? boxes.filter((item) => ids.includes(item.field.id) && item.position.page_number === page)
      : [box]
    interaction.current = {
      kind:   'move',
      page,
      start:  pointOnPage(event, page),
      origin: Object.fromEntries(moving.map((item) => [item.key, { ...item.position }])),
      moved:  false
    }
  }

  const selected_ = useMemo(() => new Set(selection), [selection])

  const handlersRef = useRef({})
  const boxHandlers = useMemo(() => ({
    move:   (...args) => handlersRef.current.move(...args),
    resize: (...args) => handlersRef.current.resize(...args)
  }), [])

  const startResize = (event, box, handle) => {
    event.stopPropagation()
    if (event.button !== 0 || readOnly) { return }

    const page = box.position.page_number
    interaction.current = {
      kind:   'resize',
      handle,
      page,
      start:  pointOnPage(event, page),
      origin: { [box.key]: { ...box.position } },
      moved:  false
    }
  }

  handlersRef.current = { move: startMove, resize: startResize }

  const startOnPage = (event, pageNumber) => {
    if (event.button !== 0) { return }
    const point = pointOnPage(event, pageNumber)
    setPending(null)

    if (!readOnly && (tool === 'draw' || target)) {
      interaction.current = { kind: 'draw', page: pageNumber, start: point, moved: false }
      setDrafts({ __draw: { page_number: pageNumber, x1: point.x, y1: point.y, x2: point.x, y2: point.y } })
      return
    }

    interaction.current = { kind: 'marquee', page: pageNumber, start: point, moved: false, additive: event.shiftKey }
  }

  const onPointerMove = useCallback((event) => {
    const current = interaction.current
    if (!current) { return }

    const point = pointOnPage(event, current.page)
    const dx = point.x - current.start.x
    const dy = point.y - current.start.y
    if (!current.moved && Math.abs(dx) * zoom < 2 && Math.abs(dy) * zoom < 2) { return }
    current.moved = true

    const threshold = SNAP_PX / zoom
    const snap = !event.altKey

    if (current.kind === 'move') {
      const ids = Object.keys(current.origin)
      const moving = Object.values(current.origin)
      const bounds = {
        x1: Math.min(...moving.map((box) => box.x1)) + dx, x2: Math.max(...moving.map((box) => box.x2)) + dx,
        y1: Math.min(...moving.map((box) => box.y1)) + dy, y2: Math.max(...moving.map((box) => box.y2)) + dy
      }
      let offsetX = 0
      let offsetY = 0
      const lines = {}
      if (snap) {
        const { xs, ys } = targetsFor(current.page, ids)
        const sx = snapAxis([bounds.x1, (bounds.x1 + bounds.x2) / 2, bounds.x2], xs, threshold)
        const sy = snapAxis([bounds.y1, (bounds.y1 + bounds.y2) / 2, bounds.y2], ys, threshold)
        if (sx) { offsetX = sx.offset; lines.x = sx.at }
        if (sy) { offsetY = sy.offset; lines.y = sy.at }
      }
      const next = {}
      Object.entries(current.origin).forEach(([id, box]) => {
        next[id] = { ...box, x1: box.x1 + dx + offsetX, x2: box.x2 + dx + offsetX, y1: box.y1 + dy + offsetY, y2: box.y2 + dy + offsetY }
      })
      setDrafts(next)
      setGuides({ page: current.page, ...lines, label: `${Math.round(bounds.x1 + offsetX)}, ${Math.round(bounds.y1 + offsetY)}` })
    }

    if (current.kind === 'resize') {
      const [id, box] = Object.entries(current.origin)[0]
      const next = { ...box }
      const handle = current.handle
      if (handle.includes('w')) { next.x1 = box.x1 + dx }
      if (handle.includes('e')) { next.x2 = box.x2 + dx }
      if (handle.includes('n')) { next.y1 = box.y1 + dy }
      if (handle.includes('s')) { next.y2 = box.y2 + dy }
      const lines = {}
      if (snap) {
        const { xs, ys } = targetsFor(current.page, [id])
        const edgeX = handle.includes('w') ? 'x1' : handle.includes('e') ? 'x2' : null
        const edgeY = handle.includes('n') ? 'y1' : handle.includes('s') ? 'y2' : null
        if (edgeX) { const s = snapAxis([next[edgeX]], xs, threshold); if (s) { next[edgeX] += s.offset; lines.x = s.at } }
        if (edgeY) { const s = snapAxis([next[edgeY]], ys, threshold); if (s) { next[edgeY] += s.offset; lines.y = s.at } }
      }
      const box_ = normalize(next)
      setDrafts({ [id]: { ...box, ...box_ } })
      setGuides({ page: current.page, ...lines, label: `${Math.round(box_.x2 - box_.x1)} × ${Math.round(box_.y2 - box_.y1)}` })
    }

    if (current.kind === 'draw') {
      const box = normalize({ x1: current.start.x, y1: current.start.y, x2: point.x, y2: point.y })
      setDrafts({ __draw: { page_number: current.page, ...box } })
      setGuides({ page: current.page, label: `${Math.round(box.x2 - box.x1)} × ${Math.round(box.y2 - box.y1)}` })
    }

    if (current.kind === 'marquee') {
      setMarquee({ page: current.page, ...normalize({ x1: current.start.x, y1: current.start.y, x2: point.x, y2: point.y }) })
    }
  }, [zoom, boxes, sizes])

  const onPointerUp = useCallback((event) => {
    const current = interaction.current
    interaction.current = null
    setGuides(null)
    if (!current) { return }
    let keepDraft = false

    if ((current.kind === 'move' || current.kind === 'resize') && current.moved) {
      const changes = {}
      Object.keys(current.origin).forEach((id) => { if (drafts[id]) { changes[id] = drafts[id] } })
      actions.updatePositions(changes)
    }

    if (current.kind === 'draw') {
      const box = drafts.__draw
      const point = current.start
      const tiny = !box || box.x2 - box.x1 < MIN_SIZE || box.y2 - box.y1 < MIN_SIZE
      if (target) {
        if (tiny) {
          actions.placeField(target.fieldId, current.page, point.x, point.y, target.option)
        } else {
          actions.updatePositions({ [boxKey(target.fieldId, target.option)]: { ...(styleClipboard || {}), page_number: current.page, ...normalize(box) } })
          actions.setActiveBox(boxKey(target.fieldId, target.option))
          actions.setPlacing(null)
        }
      } else if (!tiny) {
        setPending({ page_number: current.page, ...normalize(box) })
        keepDraft = true
      }
    }

    if (current.kind === 'marquee') {
      if (!current.moved) {
        if (!current.additive) { actions.setSelection([]) }
      } else if (marquee) {
        const inside = [...new Set(boxes.filter(({ position }) => (
          position.page_number === marquee.page &&
            position.x1 < marquee.x2 && position.x2 > marquee.x1 && position.y1 < marquee.y2 && position.y2 > marquee.y1
        )).map((box) => box.field.id))]
        actions.setSelection((selected) => (current.additive ? [...new Set([...selected, ...inside])] : inside))
      }
      setMarquee(null)
    }

    if (!keepDraft) { setDrafts({}) }
  }, [drafts, marquee, boxes, actions, target, styleClipboard])

  useEffect(() => {
    window.addEventListener('mousemove', onPointerMove)
    window.addEventListener('mouseup', onPointerUp)
    return () => {
      window.removeEventListener('mousemove', onPointerMove)
      window.removeEventListener('mouseup', onPointerUp)
    }
  }, [onPointerMove, onPointerUp])

  const PICKER_WIDTH = 340
  const pendingPickerPlace = (box, size) => {
    const right = box.x2 * zoom + 10
    const left  = right + PICKER_WIDTH < size.w * zoom ? right : Math.max(0, box.x1 * zoom - PICKER_WIDTH - 10)
    const top   = Math.max(0, Math.min(box.y1 * zoom, size.h * zoom - 420))
    return { left, top }
  }

  const targetName = ({ fieldId, option }) => {
    const field = fields.find((item) => item.id === fieldId)
    if (!field) { return '' }
    return option === null ? field.name : `${field.name} · ${optionLabel(field, option, t)}`
  }

  const createDrawn = async (dataType) => {
    const box = pending
    setPending(null)
    setDrafts({})
    setTool('select')
    await actions.createField({ dataType, position: { ...(styleClipboard || {}), ...box } })
  }

  const arrange = (kind) => {
    const arranged = selectedBoxes.map((box) => ({ id: box.key, ...box.position }))
    if (arranged.length < 2) { return }

    const changes = {}
    const set = (box, values) => { changes[box.id] = { ...box, ...values } }
    const first = arranged[0]
    const minX = Math.min(...arranged.map((box) => box.x1))
    const maxX = Math.max(...arranged.map((box) => box.x2))
    const minY = Math.min(...arranged.map((box) => box.y1))
    const maxY = Math.max(...arranged.map((box) => box.y2))

    arranged.forEach((box) => {
      const w = box.x2 - box.x1
      const h = box.y2 - box.y1
      switch (kind) {
        case 'left':    set(box, { x1: minX, x2: minX + w }); break
        case 'right':   set(box, { x1: maxX - w, x2: maxX }); break
        case 'hcenter': { const c = (minX + maxX) / 2; set(box, { x1: c - w / 2, x2: c + w / 2 }); break }
        case 'top':     set(box, { y1: minY, y2: minY + h }); break
        case 'bottom':  set(box, { y1: maxY - h, y2: maxY }); break
        case 'vcenter': { const c = (minY + maxY) / 2; set(box, { y1: c - h / 2, y2: c + h / 2 }); break }
        case 'width':   set(box, { x2: box.x1 + (first.x2 - first.x1) }); break
        case 'height':  set(box, { y2: box.y1 + (first.y2 - first.y1) }); break
        default: break
      }
    })

    if (kind === 'hspace' || kind === 'vspace') {
      const horizontal = kind === 'hspace'
      const sorted = [...arranged].sort((a, b) => (horizontal ? a.x1 - b.x1 : a.y1 - b.y1))
      const span = horizontal ? maxX - minX : maxY - minY
      const used = sorted.reduce((sum, box) => sum + (horizontal ? box.x2 - box.x1 : box.y2 - box.y1), 0)
      const gap = (span - used) / (sorted.length - 1)
      let cursor = horizontal ? minX : minY
      sorted.forEach((box) => {
        const size = horizontal ? box.x2 - box.x1 : box.y2 - box.y1
        set(box, horizontal ? { x1: cursor, x2: cursor + size } : { y1: cursor, y2: cursor + size })
        cursor += size + gap
      })
    }

    Object.values(changes).forEach((change) => { delete change.id })
    actions.updatePositions(changes)
  }

  useEffect(() => {
    const onKey = (event) => {
      if (isTyping(event.target) || event.defaultPrevented) { return }

      if (event.key === 'Escape') { setPending(null); setDrafts({}); setTool('select'); actions.setSelection([]); return }
      if (readOnly) { return }
      if (event.key === 'n' || event.key === 'N') { setTool('draw'); return }
      if (event.key === 'v' || event.key === 'V') { setTool('select'); return }

      if (workingBoxes.length === 0) { return }
      const step = event.shiftKey ? 10 : 1
      const offsets = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }
      if (offsets[event.key]) {
        event.preventDefault()
        const [dx, dy] = offsets[event.key]
        actions.updatePositions(Object.fromEntries(workingBoxes.map(({ key, position }) => (
          [key, { ...position, x1: position.x1 + dx, x2: position.x2 + dx, y1: position.y1 + dy, y2: position.y2 + dy }]
        ))))
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault()
        actions.removePositions(workingBoxes.map((box) => box.key))
      }
      if ((event.metaKey || event.ctrlKey) && event.key === 'd' && single) {
        event.preventDefault()
        actions.duplicateField(single.id)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [workingBoxes, readOnly, single, actions])

  useEffect(() => {
    const first = single && boxesOf(single)[0]
    if (!first) { return }
    const page = pageRefs.current[first.position.page_number - 1]
    const scroller = scrollRef.current
    if (!page || !scroller) { return }

    const top = page.offsetTop + first.position.y1 * zoom - 80
    const visible = top > scroller.scrollTop && top < scroller.scrollTop + scroller.clientHeight - 120
    if (!visible) { scroller.scrollTo({ top, behavior: 'smooth' }) }
  }, [single?.id])

  const onScroll = () => {
    const scroller = scrollRef.current
    const middle = scroller.scrollTop + scroller.clientHeight / 3
    const index = pageRefs.current.findIndex((page) => page && page.offsetTop + page.offsetHeight > middle)
    if (index >= 0 && index + 1 !== currentPage) { setCurrentPage(index + 1) }
  }

  const fitWidth = () => {
    const width = scrollRef.current.clientWidth - 80
    const widest = Math.max(...pages.map((_, index) => pageSize(index + 1).w))
    setZoom(Math.max(0.4, Math.min(3, Math.floor((width / widest) * 20) / 20)))
  }

  const stepZoom = (direction) => {
    const index = ZOOMS.findIndex((value) => value >= zoom - 0.001)
    const next = ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, (index < 0 ? 2 : index) + direction))]
    setZoom(next)
  }

  const onDrop = (event, pageNumber) => {
    const id = parseInt(event.dataTransfer.getData(FIELD_DRAG_TYPE), 10)
    setDropPage(null)
    if (!id || readOnly) { return }
    event.preventDefault()
    const point = pointOnPage(event, pageNumber)
    actions.placeField(id, pageNumber, point.x, point.y - 6)
  }

  const canArrange = selectedBoxes.length > 1 &&
    new Set(selectedBoxes.map((box) => box.position.page_number)).size === 1

  const toolbarButton = (icon, title, onClick, { active, disabled } = {}) => (
    <button type="button" className={`fb-tool${active ? ' active' : ''}`} title={title} onClick={onClick} disabled={disabled}>
      <i className={icon} />
    </button>
  )

  return (
    <div className="fb-canvas">
      <div className="fb-toolbar">
        <div className="fb-toolbar-group">
          {toolbarButton('fas fa-mouse-pointer', `${t.tool_select} (V)`, () => setTool('select'), { active: tool === 'select' })}
          {!readOnly && toolbarButton('far fa-plus-square', `${t.tool_draw} (N)`, () => setTool('draw'), { active: tool === 'draw' })}
        </div>
        <div className="fb-toolbar-group">
          {toolbarButton('fas fa-align-left', t.align_left, () => arrange('left'), { disabled: !canArrange })}
          {toolbarButton('fas fa-align-center', t.align_hcenter, () => arrange('hcenter'), { disabled: !canArrange })}
          {toolbarButton('fas fa-align-right', t.align_right, () => arrange('right'), { disabled: !canArrange })}
          {toolbarButton('fas fa-arrow-up', t.align_top, () => arrange('top'), { disabled: !canArrange })}
          {toolbarButton('fas fa-grip-lines', t.align_vcenter, () => arrange('vcenter'), { disabled: !canArrange })}
          {toolbarButton('fas fa-arrow-down', t.align_bottom, () => arrange('bottom'), { disabled: !canArrange })}
        </div>
        <div className="fb-toolbar-group">
          {toolbarButton('fas fa-arrows-alt-h', t.same_width, () => arrange('width'), { disabled: !canArrange })}
          {toolbarButton('fas fa-arrows-alt-v', t.same_height, () => arrange('height'), { disabled: !canArrange })}
          {toolbarButton('fas fa-ellipsis-h', t.distribute_h, () => arrange('hspace'), { disabled: selectedBoxes.length < 3 || !canArrange })}
          {toolbarButton('fas fa-ellipsis-v', t.distribute_v, () => arrange('vspace'), { disabled: selectedBoxes.length < 3 || !canArrange })}
        </div>
        <div className="fb-toolbar-group">
          {toolbarButton('fas fa-tag', t.show_labels, () => setShowLabels(!showLabels), { active: showLabels })}
        </div>
        <div className="fb-toolbar-spacer" />
        <div className="fb-toolbar-group">
          <span className="fb-toolbar-text">{interpolate(t.page_of, { page: currentPage, count: pages.length })}</span>
        </div>
        <div className="fb-toolbar-group">
          {toolbarButton('fas fa-search-minus', t.zoom_out, () => stepZoom(-1))}
          <button type="button" className="fb-tool fb-zoom" onClick={() => setZoom(1)} title="100%">{Math.round(zoom * 100)}%</button>
          {toolbarButton('fas fa-search-plus', t.zoom_in, () => stepZoom(1))}
          {toolbarButton('fas fa-expand-arrows-alt', t.fit_width, fitWidth)}
        </div>
      </div>

      {(target || tool === 'draw') &&
        <div className="fb-canvas-banner">
          <i className="fas fa-crosshairs" />
          {target ? interpolate(t.place_hint, { name: targetName(target) }) : t.draw_hint}
          {placing && <button type="button" className="fb-link" onClick={() => actions.setPlacing(null)}>{t.cancel}</button>}
        </div>
      }

      <div className={`fb-canvas-scroll${tool === 'draw' || target ? ' is-drawing' : ''}`} ref={scrollRef} onScroll={onScroll}>
        {pages.map((page, index) => {
          const pageNumber = index + 1
          const size = pageSize(pageNumber)
          const draw = drafts.__draw && drafts.__draw.page_number === pageNumber ? drafts.__draw : (pending && pending.page_number === pageNumber ? pending : null)

          return (
            <div key={page.url} className="fb-page-wrap">
              <div className="fb-page-number">{pageNumber}</div>
              <div
                className={`fb-page${dropPage === pageNumber ? ' is-drop' : ''}`}
                ref={(element) => { pageRefs.current[index] = element }}
                style={{ width: size.w * zoom, height: size.h * zoom }}
                onMouseDown={(event) => startOnPage(event, pageNumber)}
                onDragOver={(event) => {
                  if (!readOnly && event.dataTransfer.types.includes(FIELD_DRAG_TYPE)) { event.preventDefault(); setDropPage(pageNumber) }
                }}
                onDragLeave={() => setDropPage(null)}
                onDrop={(event) => onDrop(event, pageNumber)}
              >
                <img
                  src={page.url}
                  alt=""
                  draggable={false}
                  onLoad={(event) => {
                    const { naturalWidth: w, naturalHeight: h } = event.target
                    setSizes((current) => ({ ...current, [pageNumber]: { w, h } }))
                  }}
                />

                {boxes.filter((box) => box.position.page_number === pageNumber).map((box) => {
                  const selected = selected_.has(box.field.id)
                  const active = selected && box.key === activeBox
                  return (
                    <Box
                      key={box.key}
                      box={box}
                      selected={selected}
                      active={active}
                      handles={selected && selection.length === 1 && (active || selectedBoxes.length === 1) && !readOnly}
                      zoom={zoom}
                      showLabels={showLabels}
                      handlers={boxHandlers}
                    />
                  )
                })}

                {draw && (
                  <div className="fb-box is-draft" style={{ left: draw.x1 * zoom, top: draw.y1 * zoom, width: (draw.x2 - draw.x1) * zoom, height: (draw.y2 - draw.y1) * zoom }} />
                )}

                {pending && pending.page_number === pageNumber && (
                  <div
                    className="fb-pending-picker"
                    style={pendingPickerPlace(pending, size)}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <TypePicker
                      exclude={['page_break']}
                      onPick={createDrawn}
                      onClose={() => { setPending(null); setDrafts({}) }}
                      extra={<div className="fb-type-picker-title">{t.what_goes_here}</div>}
                    />
                  </div>
                )}

                {marquee && marquee.page === pageNumber && (
                  <div className="fb-marquee" style={{ left: marquee.x1 * zoom, top: marquee.y1 * zoom, width: (marquee.x2 - marquee.x1) * zoom, height: (marquee.y2 - marquee.y1) * zoom }} />
                )}

                {guides && guides.page === pageNumber && (
                  <>
                    {guides.x !== undefined && <div className="fb-guide is-x" style={{ left: guides.x * zoom }} />}
                    {guides.y !== undefined && <div className="fb-guide is-y" style={{ top: guides.y * zoom }} />}
                  </>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {guides && guides.label && <div className="fb-measure">{guides.label}</div>}
    </div>
  )
}

export default PdfCanvas
